import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { LocaleApplyService } from '@app/common/locale/application/services/locale-apply.service';
import { PublicLocaleStrategy } from '@app/common/locale/application/strategies/public-locale.strategy';
import { UrlOnlyLocaleStrategy } from '@app/common/locale/application/strategies/url-only-locale.strategy';
import { DEFAULT_PUBLIC_LOCALE } from '@app/common/locale/domain/constants/supported-locales';
import { Locale } from '@app/common/locale/domain/entities/locale';
import {
    LocaleResolutionPolicy,
    LocaleResolutionResult,
} from '@app/common/locale/domain/entities/locale-resolution-policy';

/**
 * Unica entrada de resolucion de idioma para rutas publicas: despacha segun la politica de la ruta
 * y aplica el resultado (estado + idioma de UI) para que ambos queden en el mismo idioma.
 * UserOnly no pasa por aca: el idioma del usuario lo posee DashboardLocaleStrategy.
 */
@Injectable({ providedIn: 'root' })
export class LocaleResolutionService {
    private readonly urlOnlyStrategy: UrlOnlyLocaleStrategy = new UrlOnlyLocaleStrategy();

    public constructor(
        private readonly publicLocaleStrategy: PublicLocaleStrategy,
        private readonly localeApplyService: LocaleApplyService,
        @Inject(PLATFORM_ID) private readonly platformId: object,
    ) {}

    public resolve(
        url: string,
        policy: Exclude<LocaleResolutionPolicy, LocaleResolutionPolicy.UserOnly>,
    ): Observable<LocaleResolutionResult> {
        const result$: Observable<LocaleResolutionResult> =
            policy === LocaleResolutionPolicy.UrlOnly
                ? of(this.urlOnlyStrategy.resolve(url))
                : this.publicLocaleStrategy
                      .resolveFromUrlOrStorage(url, isPlatformBrowser(this.platformId) ? null : '')
                      .pipe(
                          map(
                              (locale: Locale): LocaleResolutionResult => ({
                                  locale,
                                  redirectTo: null,
                                  notFound: false,
                              }),
                          ),
                      );

        return result$.pipe(
            tap((result: LocaleResolutionResult): void => {
                if (result.redirectTo === null && !result.notFound) {
                    this.localeApplyService.apply(result.locale);
                }
            }),
        );
    }

    /** Ante un error inesperado de resolucion: espanol internacional en vez de dejar el idioma sin definir. */
    public applyDefault(): void {
        this.localeApplyService.apply(Locale.fromString(DEFAULT_PUBLIC_LOCALE));
    }
}
